import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingUp, AlertCircle, ArrowRight, Check } from 'lucide-react';
import { pricingComparison } from '../data/mock';

export default function ROICalculatorPage() {
  const [teamSize, setTeamSize] = useState(5);
  const [stage, setStage] = useState('seed');

  const calculateSavings = () => {
    const baseSavings = pricingComparison.fractional.savings;
    const stageMult = { 'bootstrap': 0.8, 'pre-seed': 0.9, 'seed': 1.0, 'series-a': 1.1, 'series-b': 1.2 };
    return Math.round(baseSavings * (stageMult[stage] || 1));
  };

  return (
    <div>
      {/* Page Header */}
      <section className="hero-gradient pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Calculator className="w-12 h-12 text-accent mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="roi-calculator-title">
            ROI Calculator
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            See how much you can save with fractional leadership.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="calculator-section">
        <div className="max-w-5xl mx-auto">
          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-lg mx-auto mb-16">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Team Size</label>
              <select
                value={teamSize}
                onChange={(e) => setTeamSize(Number(e.target.value))}
                data-testid="team-size-select"
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              >
                <option value={3}>1-5 people</option>
                <option value={10}>6-15 people</option>
                <option value={25}>16-30 people</option>
                <option value={40}>30+ people</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Company Stage</label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                data-testid="company-stage-select"
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              >
                <option value="bootstrap">Bootstrapped</option>
                <option value="pre-seed">Pre-Seed</option>
                <option value="seed">Seed</option>
                <option value="series-a">Series A</option>
                <option value="series-b">Series B</option>
              </select>
            </div>
          </div>

          {/* Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Full-Time */}
            <div className="card-depth rounded-2xl p-8 border-red-200 bg-red-50/30" data-testid="fulltime-card">
              <h3 className="text-xl font-bold text-foreground mb-8">Full-Time Executive</h3>
              <div className="space-y-4 mb-6">
                {[
                  { label: 'Base Salary', amount: pricingComparison.fullTime.salary },
                  { label: 'Benefits & Taxes', amount: pricingComparison.fullTime.benefits },
                  { label: 'Equity (4-year vest)', amount: pricingComparison.fullTime.equity },
                  { label: 'Recruiting Costs', amount: pricingComparison.fullTime.recruiting },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between items-center pb-4 border-b border-border text-sm">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium text-foreground">${item.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4 border-t-2 border-border">
                <span className="font-bold text-foreground">Total Annual Cost</span>
                <span className="text-2xl font-bold text-foreground">${pricingComparison.fullTime.total.toLocaleString()}</span>
              </div>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="w-4 h-4" />
                  <span>{pricingComparison.fullTime.timeToHire} to hire</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <span>{pricingComparison.fullTime.riskLevel} Risk</span>
                </div>
              </div>
            </div>

            {/* Fractional */}
            <div className="card-depth rounded-2xl p-8 border-accent/30 bg-accent/5 relative" data-testid="fractional-card">
              <div className="absolute -top-3 right-8 bg-accent text-white px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                Recommended
              </div>
              <h3 className="text-xl font-bold text-foreground mb-8">Fractional Executive</h3>
              <div className="space-y-4 mb-6">
                {[
                  { label: 'Annual Engagement', amount: pricingComparison.fractional.cost },
                  { label: 'Benefits & Taxes', amount: 0 },
                  { label: 'Equity', amount: 0 },
                  { label: 'Recruiting Costs', amount: 0 },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between items-center pb-4 border-b border-border text-sm">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium text-foreground">${item.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4 border-t-2 border-accent/30">
                <span className="font-bold text-primary">Total Annual Cost</span>
                <span className="text-2xl font-bold text-primary">${pricingComparison.fractional.total.toLocaleString()}</span>
              </div>
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-2 text-sm text-accent">
                  <TrendingUp className="w-4 h-4" />
                  <span>{pricingComparison.fractional.timeToStart} to start</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-accent">
                  <Check className="w-4 h-4" />
                  <span>{pricingComparison.fractional.riskLevel} Risk</span>
                </div>
              </div>
            </div>
          </div>

          {/* Savings Highlight */}
          <div className="text-center mb-16" data-testid="savings-highlight">
            <div className="inline-block rounded-2xl border-2 border-accent bg-accent/5 px-12 py-10 lg:px-20 lg:py-14">
              <div className="text-5xl lg:text-6xl font-bold text-accent mb-3 stat-counter">
                ${calculateSavings().toLocaleString()}
              </div>
              <div className="text-xl font-bold text-foreground mb-3">
                Annual Savings ({pricingComparison.fractional.savingsPercent}%)
              </div>
              <p className="text-muted-foreground max-w-md mx-auto">
                Invest these savings back into your product, marketing, or additional team members.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/contact"
              data-testid="roi-cta-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-white font-semibold text-lg px-8 py-4 hover:bg-primary/90 hover-lift transition-all group"
            >
              Get Your Custom Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
